import React, { useState } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { Button, Card, Flex, Space, Spin, Typography } from 'antd';
import { isEmpty } from 'lodash';

import { useGetErrorNotifs } from '../api/getErrorNotifs';
import { IErrorNotif } from '../types';
import { ErrorNotifItem } from './ErrorNotifItem';
import { ErrorNotifModal } from './ErrorNotifModal';
import { scopes } from '@/config';
import useAppState from '@/hooks/useAppState';

interface IErrorNotifsSectionProps {
  partnerId: string;
}

export const ErrorNotifsSection = ({ partnerId }: IErrorNotifsSectionProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingNotif, setEditingNotif] = useState<IErrorNotif | null>(null);

  const { data: notifs, isLoading } = useGetErrorNotifs(partnerId);
  const userScopes = useAppState((state) => state.scopes);
  const canManage = userScopes.includes(scopes.MANAGE_WEBHOOKS);

  const handleEdit = (notif: IErrorNotif) => {
    setEditingNotif(notif);
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setEditingNotif(null);
  };

  return (
    <Card title="Error Notification Emails" data-testid="error-notifs-section">
      <Space direction="vertical" style={{ width: '100%' }}>
        <Typography.Text
          type="secondary"
          style={{ marginBottom: 16, display: 'inline-block' }}
        >
          Receive an email notification when a webhook delivery fails.
        </Typography.Text>
        {isLoading ? (
          <Flex justify="center" style={{ padding: 24 }}>
            <Spin />
          </Flex>
        ) : isEmpty(notifs) ? (
          <Space direction="vertical" style={{ marginBottom: '1rem' }}>
            <Typography.Text>No error notifications found.</Typography.Text>
          </Space>
        ) : (
          (notifs ?? []).map((notif) => (
            <ErrorNotifItem
              key={notif.notif_id}
              notif={notif}
              partnerId={partnerId}
              onEdit={handleEdit}
            />
          ))
        )}

        {canManage && (
          <Button
            type="link"
            icon={<PlusOutlined />}
            onClick={() => setModalOpen(true)}
            data-testid="error-notif-add-button"
          >
            Add new email
          </Button>
        )}

        <ErrorNotifModal
          partnerId={partnerId}
          notif={editingNotif}
          open={modalOpen}
          onClose={handleClose}
        />
      </Space>
    </Card>
  );
};
